import { useState } from 'react';
import { useAppStore } from '../../services/store';
import type { AppRequest } from '../../services/types';

interface RequestManagementProps {
  filter?: string;
}

export default function RequestManagement({ filter: propFilter = 'All' }: RequestManagementProps) {
  const { requests, updateRequestStatus } = useAppStore();
  const [filter, setFilter] = useState(propFilter);
  const [selectedRequest, setSelectedRequest] = useState<AppRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [adminNotes, setAdminNotes] = useState('');

  const filteredRequests = requests.filter(request => {
    if (filter === 'All' || filter === 'all') return true;
    if (filter === 'pending') return request.status === 'Pending';
    if (filter === 'approved') return request.status === 'Approved';
    if (filter === 'rejected') return request.status === 'Rejected';
    return request.status === filter;
  });

  const handleAction = (request: AppRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminNotes('');
    setShowModal(true);
  };

  const handleSubmitAction = () => {
    if (!selectedRequest) return;

    const newStatus = actionType === 'approve' ? 'Approved' : 'Rejected';
    updateRequestStatus(selectedRequest.id, newStatus, adminNotes);

    setShowModal(false);
    setSelectedRequest(null);
    setAdminNotes('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Medium':
        return 'bg-blue-100 text-blue-800';
      case 'Low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          App Requests ({filteredRequests.length} requests)
        </h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 pr-8"
        >
          <option value="All">All Requests</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {filteredRequests.map((request) => (
            <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h4 className="text-lg font-semibold text-gray-900">{request.appName}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(request.priority)}`}>
                      {request.priority}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">{request.description}</p>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-gray-900 mb-2">Business Justification</h5>
                    <p className="text-sm text-gray-700 leading-relaxed">{request.businessJustification}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Requester:</span> {request.requesterName}
                    </div>
                    <div>
                      <span className="font-medium">Email:</span> {request.requesterEmail}
                    </div>
                    <div>
                      <span className="font-medium">Department:</span> {request.department}
                    </div>
                    <div>
                      <span className="font-medium">Expected Users:</span> {request.expectedUsers}
                    </div>
                    <div>
                      <span className="font-medium">Category:</span> {request.category}
                    </div>
                    <div>
                      <span className="font-medium">Request Date:</span> {new Date(request.requestDate).toLocaleDateString()}
                    </div>
                  </div>

                  {request.adminNotes && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h5 className="font-medium text-blue-900 mb-2">Admin Notes</h5>
                      <p className="text-sm text-blue-800">{request.adminNotes}</p>
                    </div>
                  )}
                </div>

                {request.status === 'Pending' && (
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleAction(request, 'approve')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      <i className="ri-check-line mr-1"></i>
                      Approve
                    </button>
                    <button
                      onClick={() => handleAction(request, 'reject')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      <i className="ri-close-line mr-1"></i>
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests found</h3>
            <p className="text-gray-600">
              {filter === 'All' ? 'No app requests have been submitted yet' : `No ${filter.toLowerCase()} requests found`}
            </p>
          </div>
        )}
      </div>

      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedRequest.appName} by {selectedRequest.requesterName}
              </p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes {actionType === 'reject' ? '*' : '(Optional)'}
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={
                  actionType === 'approve'
                    ? "Add any additional notes or instructions..."
                    : "Please provide a reason for rejection..."
                }
                required={actionType === 'reject'}
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAction}
                disabled={actionType === 'reject' && !adminNotes.trim()}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium whitespace-nowrap ${
                  actionType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
